/**
 * PromoCard — dispatcher that picks the right variant per promo.kind.
 * Outer chrome uses a kind-themed gradient hero band. Body per kind:
 *   - welcome / first_deposit / reload → ThreeStepStepper
 *   - cashback / turnover_rebate → RebateBody (period accrual from rebate-preview)
 *   - daily_checkin → CheckinCalendar
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Gift,
  TrendingUp,
  RotateCw,
  BadgePercent,
  CalendarCheck,
  Coins,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import {
  bigIntish,
  turnoverProgress,
  isPromoExpired,
  promoEndsAt,
  fetchRebatePreview,
  fetchCheckinStreak,
  PROMO_QUERY_KEYS,
  type Promotion,
  type PromotionClaim,
} from '@/features/promo/api.js';
import {
  checkinGateLines,
  infoLines,
  rebateCaptionKey,
  rebateDisplayMinor,
  REBATE_CAPTION_FALLBACKS,
} from '@/features/promo/card-copy.js';
import { formatDate } from '@/lib/datetime/format.js';
import { toBigOrNull as toBig } from '@/features/promo/reward-rules.js';
import { rewardHeadlineFor } from '@/features/promo/headline.js';
import { nextActionFor, currentStepFor, type PromoKind } from '@/features/promo/claim-instructions.js';
import { useFormatMoney } from '@/components/money/MoneyProvider.js';
import { ThreeStepStepper } from './steppers/ThreeStepStepper.js';
import { CheckinCalendar } from './steppers/CheckinCalendar.js';

// Gradients reference the brand ramp (--art-*, derived from --primary/--accent)
// so promo hero bands follow the tenant theme. Icons stay per-kind.
const KIND_THEME: Record<string, { gradient: string; Icon: LucideIcon }> = {
  welcome:         { gradient: 'var(--art-1)', Icon: Gift },
  first_deposit:   { gradient: 'var(--art-2)', Icon: TrendingUp },
  reload:          { gradient: 'var(--art-3)', Icon: RotateCw },
  cashback:        { gradient: 'var(--art-4)', Icon: BadgePercent },
  turnover_rebate: { gradient: 'var(--art-5)', Icon: Coins },
  daily_checkin:   { gradient: 'var(--art-6)', Icon: CalendarCheck },
};

const FALLBACK_THEME = KIND_THEME['reload'];

interface Props {
  promo: Promotion;
  claim?: PromotionClaim | null;
  currency: string;
  claiming?: boolean;
  onClaim: (promo: Promotion) => void;
}

export function PromoCard({ promo, claim, currency, claiming, onClaim }: Props) {
  const { t, i18n } = useTranslation('promo');
  const formatMoney = useFormatMoney();
  const navigate = useNavigate();
  // daily_checkin reward amounts come from the streak endpoint. The two states
  // read different fields: "check in today" wants the day being priced now,
  // "back tomorrow" wants the one after it. They were the same field back when
  // the card's day number ran one ahead of the ledger's (#366).
  // Shares CheckinBody's cache key → one fetch.
  const checkinStreak = useQuery({
    queryKey: PROMO_QUERY_KEYS.checkinStreak(promo.id),
    queryFn: () => fetchCheckinStreak(promo.id),
    staleTime: 30_000,
    enabled: promo.kind === 'daily_checkin',
  });
  const checkinAmount = checkinStreak.data
    ? (toBig(checkinStreak.data.amountToday) ?? undefined)
    : undefined;
  // Falls back to today's amount, never to zero: the two apps deploy on
  // independent pipelines, so a client that ships before svc-promo sees no
  // amountNextDay at all, and `?? 0n` downstream would put ฿0.00 on a reward
  // line. A stale-by-one-day figure is wrong; a zero reward is a lie.
  //
  // A zero takes the same path as an absent field on purpose. An operator who
  // clears `amounts` on a running promotion turns every figure to '0', which is
  // present and therefore slips past `??` — the exact ฿0.00 this guard exists
  // to prevent. When today's amount is zero too, nextActionFor renders nothing
  // rather than a figure neither side can honour.
  const nextDayRaw = checkinStreak.data ? toBig(checkinStreak.data.amountNextDay) : null;
  const checkinNextAmount = checkinStreak.data
    ? (nextDayRaw !== null && nextDayRaw > 0n ? nextDayRaw : checkinAmount)
    : undefined;
  const action = nextActionFor({
    kind: promo.kind as PromoKind,
    claim: claim ?? null,
    dayAmount: checkinAmount,
    nextDayAmount: checkinNextAmount,
    claimedToday: checkinStreak.data?.claimedToday,
    money: (v) => formatMoney(v, currency),
  });

  // The eligibility gates, said before the press rather than after it. Without
  // this the only signal a member got was a generic red toast on a 403, which
  // names neither the requirement nor the amount.
  //
  // `=== false` is the whole guard: an ABSENT field is a server that predates
  // it, and the two apps deploy on independent pipelines. Treating "unknown" as
  // "ineligible" would dark every check-in button on the platform for the length
  // of a rollout.
  const checkinBlocked = checkinStreak.data?.eligibility?.eligible === false;
  const gateLines = checkinBlocked
    ? checkinGateLines(checkinStreak.data?.eligibility, t, (v) => formatMoney(v, currency))
    : [];

  const theme = KIND_THEME[promo.kind] ?? FALLBACK_THEME;
  const { gradient, Icon } = theme;
  const headline = rewardHeadlineFor(promo, currency, t, (m) => formatMoney(m, currency));

  // Rebate kinds gate the claim CTA on the preview: only claimable when an
  // accrued, unclaimed amount exists (else the server returns 400). Shares the
  // cache key with RebateBody, so this is one fetch, not two.
  const isRebate = promo.kind === 'cashback' || promo.kind === 'turnover_rebate';
  const rebatePreview = useQuery({
    queryKey: PROMO_QUERY_KEYS.rebatePreview(promo.id),
    queryFn: () => fetchRebatePreview(promo.id),
    staleTime: 60_000,
    enabled: isRebate && !claim,
  });
  const rebateBlocked = isRebate && !claim && rebatePreview.data != null && !rebatePreview.data.claimable;

  // The server drops out-of-window promotions from the player list, but a card
  // can outlive that boundary in an open tab (60s staleTime, and the list is
  // not refetched on a timer). Claiming then fails with PROMO-010, so the CTA
  // dies here rather than round-tripping to an error banner.
  const endsAt = promoEndsAt(promo);
  const expired = isPromoExpired(promo);

  const handleClick = () => {
    if (action.action === 'claim') onClaim(promo);
    else if (action.href) void navigate({ to: action.href });
  };

  return (
    <article className="h-full rounded-2xl overflow-hidden bg-[var(--bg-raised)] flex flex-col">
      <div className="relative px-3.5 py-2.5" style={{ background: gradient }}>
        <div className="flex items-center gap-1.5 text-white/85 text-[10px] font-bold uppercase tracking-wide">
          <Icon size={12} />
          {t(`kind.${promo.kind}`, { defaultValue: promo.kind })}
        </div>
        <div className="flex items-baseline justify-between gap-2 mt-0.5">
          <div className="text-white font-extrabold text-[16px] leading-tight drop-shadow-[0_1px_3px_rgba(0,0,0,.4)] truncate">
            {headline}
          </div>
          <div className="text-white/80 text-[10.5px] truncate shrink min-w-0">{promo.name}</div>
        </div>
      </div>

      <div className="p-3.5 flex flex-col gap-2.5 flex-1">
        <PromoBody promo={promo} claim={claim ?? null} currency={currency} />

        <PromoInfo promo={promo} currency={currency} />

        {/* CTA / footnote pinned to bottom so cards in a row align */}
        <div className="mt-auto">
          {endsAt && (
            <div className="mb-1.5 text-[10.5px] text-[var(--text-muted)] text-center">
              {expired
                ? t('active.expired')
                : t('active.ends', { date: formatDate(endsAt, i18n.language) })}
            </div>
          )}
          {gateLines.length > 0 && (
            <p
              role="status"
              className="mb-1.5 rounded-xl bg-[var(--warning-bg)] px-2.5 py-1.5 text-center text-[11px] leading-relaxed text-[var(--warning)]"
            >
              {gateLines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </p>
          )}
          {action.cta && (
            <Button
              size="sm"
              variant="primary"
              className="w-full"
              disabled={!!claiming || rebateBlocked || expired || checkinBlocked}
              onClick={handleClick}
            >
              {claiming ? t('active.claiming') : t(action.cta, action.params as Record<string, unknown>)}
            </Button>
          )}
          {!action.cta && action.i18nKey && (
            <div className="text-[11px] text-[var(--text-muted)] text-center">
              {t(action.i18nKey, action.params as Record<string, unknown>)}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function PromoBody({
  promo,
  claim,
  currency,
}: {
  promo: Promotion;
  claim: PromotionClaim | null;
  currency: string;
}) {
  const { t } = useTranslation('promo');
  const formatMoney = useFormatMoney();

  switch (promo.kind) {
    case 'welcome':
    case 'first_deposit':
    case 'reload': {
      const mult = promo.turnoverMultiplier ? Number(promo.turnoverMultiplier) : 5;
      const conds = (promo.conditions ?? {}) as Record<string, unknown>;
      const minDepRaw = conds['minDeposit'] as number | string | undefined;
      const minDepMinor = toBig(minDepRaw);
      const minAmount = minDepMinor !== null ? formatMoney(minDepMinor, currency) : `${mult}×`;
      // Currency-aware target — formatMoney respects minor-unit exponent
      // so JPY/THB/TND all render correctly.
      const target = claim
        ? formatMoney(bigIntish(claim.turnoverRequired), claim.currency ?? currency)
        : `${mult}×`;
      const currentStep = currentStepFor(promo.kind as PromoKind, claim);
      const progress = claim ? turnoverProgress(claim) : 0;
      return (
        <ThreeStepStepper
          steps={[
            { label: t(`card.${promo.kind}.step1`, { mult, minAmount }) },
            { label: t(`card.${promo.kind}.step2`, { mult, minAmount, target }) },
            { label: t(`card.${promo.kind}.step3`, { mult, minAmount, target }) },
          ]}
          currentStep={currentStep}
          progress={progress}
        />
      );
    }
    case 'cashback':
    case 'turnover_rebate':
      return <RebateBody promo={promo} claim={claim} currency={currency} />;
    case 'daily_checkin':
      return <CheckinBody promo={promo} currency={currency} />;
    default:
      return null;
  }
}

function RebateBody({
  promo,
  claim,
  currency,
}: {
  promo: Promotion;
  claim: PromotionClaim | null;
  currency: string;
}) {
  const { t } = useTranslation('promo');
  const formatMoney = useFormatMoney();
  const preview = useQuery({
    queryKey: PROMO_QUERY_KEYS.rebatePreview(promo.id),
    queryFn: () => fetchRebatePreview(promo.id),
    staleTime: 60_000,
    enabled: !claim,
  });

  if (claim) return null;
  if (preview.isPending) {
    return <div className="h-[60px] rounded-md bg-[var(--bg-elevated)]/40 animate-pulse" />;
  }
  const p = preview.data;
  const amount = formatMoney(rebateDisplayMinor(p), currency);
  const captionKey = rebateCaptionKey(p);
  return (
    <div className="rounded-lg bg-[var(--bg-elevated)]/40 p-3 text-center">
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
        {t('card.rebate.thisPeriod', { defaultValue: 'This period' })}
      </div>
      <div className="text-[20px] font-extrabold text-[var(--text-primary)] mt-0.5">{amount}</div>
      {captionKey && (
        <div className="text-[10.5px] text-[var(--text-muted)] mt-0.5">
          {t(captionKey, { defaultValue: REBATE_CAPTION_FALLBACKS[captionKey] })}
        </div>
      )}
    </div>
  );
}

function CheckinBody({
  promo,
  currency,
}: {
  promo: Promotion;
  currency: string;
}) {
  const streak = useQuery({
    queryKey: PROMO_QUERY_KEYS.checkinStreak(promo.id),
    queryFn: () => fetchCheckinStreak(promo.id),
    staleTime: 30_000,
  });

  if (!streak.data) {
    return <div className="h-[60px] rounded-md bg-[var(--bg-elevated)]/40 animate-pulse" />;
  }
  return (
    <CheckinCalendar
      schedule={streak.data.schedule.map((s) => toBig(s) ?? 0n)}
      currentDay={streak.data.currentDay}
      daysClaimed={streak.data.daysClaimed}
      currency={currency}
    />
  );
}

function PromoInfo({ promo, currency }: { promo: Promotion; currency: string }) {
  const { t } = useTranslation('promo');
  const formatMoney = useFormatMoney();
  const lines = infoLines(promo, currency, t, (m) => formatMoney(m, currency));
  if (lines.length === 0) return null;
  return (
    <details className="group rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)]/40">
      <summary className="flex items-center justify-between gap-2 cursor-pointer select-none list-none px-3 py-2 text-[11px] font-semibold text-[var(--text-secondary)] [&::-webkit-details-marker]:hidden">
        {t('card.info.title')}
        <ChevronDown size={13} className="shrink-0 transition-transform duration-200 group-open:rotate-180" />
      </summary>
      <ul className="flex flex-col gap-1.5 px-3 pb-2.5 pt-0.5 text-[11px] leading-relaxed text-[var(--text-secondary)]">
        {lines.map((line, i) => (
          <li key={i} className="flex gap-1.5">
            <span aria-hidden className="text-[var(--text-muted)]">·</span>
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}
